import React, { useState, useEffect } from "react";
import { Client } from "@/types";
import { apiClient } from "@/services/apiClient";
import { EditIcon, SpinnerIcon } from "@/components/icons";
import { CaptionConfigModal } from "./CaptionConfigModal";

interface CaptionConfigEditorProps {
  client: Client;
  onSave?: () => void;
}

interface CaptionConfig {
  brandVoice: string;
  targetAudience: string;
  hashtags: string[];
  location: string;
  brandDescription: string;
  writingGuidelines: string;
}

interface CaptionConfigResponse {
  success: boolean;
  captionConfig: CaptionConfig;
}

export const CaptionConfigEditor: React.FC<CaptionConfigEditorProps> = ({
  client,
  onSave,
}) => {
  const [config, setConfig] = useState<CaptionConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchCaptionConfig();
  }, [client.id]);

  const fetchCaptionConfig = async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.get<CaptionConfigResponse>(
        `/client/${client.id}/caption-config`
      );

      if (data.success && data.captionConfig) {
        setConfig(data.captionConfig);
      }
    } catch (error) {
      console.error("Error fetching caption config:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaved = () => {
    fetchCaptionConfig();
    onSave?.();
  };

  const hasConfig =
    config &&
    (config.brandVoice ||
      config.brandDescription ||
      config.targetAudience ||
      config.location ||
      (config.hashtags && config.hashtags.length > 0) ||
      config.writingGuidelines);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Caption Generation
        </h3>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
        >
          <EditIcon className="w-3.5 h-3.5" />
          Edit
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <SpinnerIcon className="w-4 h-4" />
          <span className="text-sm">Loading...</span>
        </div>
      ) : !hasConfig ? (
        <p className="text-sm text-gray-500">
          No caption configuration set. Click Edit to configure brand voice,
          audience, hashtags, and more.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {config.brandVoice && (
            <div>
              <span className="text-gray-500">Brand Voice</span>
              <p className="font-medium text-gray-900">{config.brandVoice}</p>
            </div>
          )}

          {config.location && (
            <div>
              <span className="text-gray-500">Location</span>
              <p className="font-medium text-gray-900">{config.location}</p>
            </div>
          )}

          {config.targetAudience && (
            <div className="sm:col-span-2">
              <span className="text-gray-500">Target Audience</span>
              <p className="font-medium text-gray-900 line-clamp-2">
                {config.targetAudience}
              </p>
            </div>
          )}

          {config.brandDescription && (
            <div className="sm:col-span-2">
              <span className="text-gray-500">Brand Description</span>
              <p className="font-medium text-gray-900 line-clamp-2">
                {config.brandDescription}
              </p>
            </div>
          )}

          {config.hashtags && config.hashtags.length > 0 && (
            <div className="sm:col-span-2">
              <span className="text-gray-500">Hashtags</span>
              <p className="font-medium text-gray-900 line-clamp-2">
                {config.hashtags.join(", ")}
              </p>
            </div>
          )}

          {config.writingGuidelines && (
            <div className="sm:col-span-2">
              <span className="text-gray-500">Writing Guidelines</span>
              <p className="font-medium text-gray-900 line-clamp-2">
                {config.writingGuidelines}
              </p>
            </div>
          )}
        </div>
      )}

      <CaptionConfigModal
        isOpen={showModal}
        client={client}
        onClose={() => setShowModal(false)}
        onSaved={handleSaved}
      />
    </div>
  );
};
